import { findBuildingDetails, getChildText, getChildrenByLocalName, parseNumber } from "./hpxmlDom";

const FT2_TO_M2 = 0.09290304;
/** HPXML assembly R is imperial (F·ft²·h/Btu); divide by 5.678 for RSI (m²·K/W). */
const R_IMP_PER_RSI = 5.678;

export const imperialRToRsi = (rImp) => (rImp == null || !Number.isFinite(rImp) ? null : rImp / R_IMP_PER_RSI);
export const ft2ToM2 = (ft2) => (ft2 == null || !Number.isFinite(ft2) ? null : ft2 * FT2_TO_M2);

function getAssemblyEffectiveRValue(surfaceEl) {
    const insulation = getChildrenByLocalName(surfaceEl, "Insulation")[0];
    if (!insulation) return null;
    return parseNumber(getChildText(insulation, "AssemblyEffectiveRValue"));
}

function getNominalRValue(surfaceEl) {
    const insulation = getChildrenByLocalName(surfaceEl, "Insulation")[0];
    if (!insulation) return null;
    return parseNumber(getChildText(insulation, "NominalRValue"));
}

function getWindowUFactor(window) {
    return 1 / parseNumber(getChildText(window, "UFactor"));
}

function getDoorRValue(door) {
    return parseNumber(getChildText(door, "RValue"));
}

function getAirLeakageRate(airInfiltration) {
    const buildingAirLeakage = getChildrenByLocalName(airInfiltration, "BuildingAirLeakage")[0];

    if (!buildingAirLeakage) return null;

    return parseNumber(getChildText(buildingAirLeakage, "AirLeakage"));
}

function getSlabRValue(slab) {
    const insulation = getChildrenByLocalName(slab, "UnderSlabInsulation")[0];
    if (!insulation) return null;
    const layer = getChildrenByLocalName(insulation, "Layer")[0];
    if (!layer) return null;
    return parseNumber(getChildText(layer, "NominalRValue"));
}

function aggregateSurfaces(elements, getArea, getR, componentType = "None") {
    const details = [];
    for (const el of elements) {
        const area = getArea(el);
        const r = getR(el);

        if (area != null && area > 0) {
            details.push({
                areaFt2: area,
                areaM2: ft2ToM2(area),
                rImp: r != null && Number.isFinite(r) ? r : null,
            });
        }
    }
    details.sort((a, b) => b.areaM2 - a.areaM2);

    const totalFt2 = details.reduce((s, d) => s + d.areaFt2, 0);
    let sumRFt2 = 0;
    let ft2WithR = 0;
    for (const d of details) {
        if (d.rImp != null) {
            sumRFt2 += d.areaFt2 * d.rImp;
            ft2WithR += d.areaFt2;
        }
    }
    const areaWeightedRImp = ft2WithR > 0 ? sumRFt2 / ft2WithR : null;

    return {
        count: details.length,
        totalFt2,
        totalM2: ft2ToM2(totalFt2),
        areaWeightedRImp,
        areaWeightedRsi: imperialRToRsi(areaWeightedRImp),
        details,
    };
}

function enclosureChild(enclosure, name) {
    return getChildrenByLocalName(enclosure, name)[0];
}

function collectSurfaces(enclosure, groupLocal, surfaceLocal) {
    const group = enclosureChild(enclosure, groupLocal);
    if (!group) return [];
    return getChildrenByLocalName(group, surfaceLocal);
}

function isAtticAdjacent(s) {
    return typeof s === "string" && s.toLowerCase().includes("attic");
}

function mentionsBasement(s) {
    return typeof s === "string" && s.toLowerCase().includes("basement");
}

function mentionsCrawlspace(s) {
    return typeof s === "string" && s.toLowerCase().includes("crawlspace");
}

function mentionsGarage(s) {
    return typeof s === "string" && s.toLowerCase().includes("garage");
}

function isOtherHousingUnit(s) {
    return typeof s === "string" && s.toLowerCase() === "other housing unit";
}

function getH2kLabel(el) {
    const extension = getChildrenByLocalName(el, "extension")[0];
    return extension ? getChildText(extension, "H2kLabel") : "";
}

/**
 * The translator stamps a basement's H2K label onto its FoundationWall, Slab, and pony Wall, so a
 * wall sharing a label with both a foundation wall and a slab is that foundation's pony wall.
 */
function getPonyWallLabels(foundationWalls, slabs) {
    const slabLabels = new Set(slabs.map(getH2kLabel).filter(Boolean));
    return new Set(foundationWalls.map(getH2kLabel).filter((label) => label && slabLabels.has(label)));
}

/**
 * Map HPXML BuildingDetails/Enclosure aggregates to the same row ids as `envelopeRowHeadings`
 * (approximate grouping — translator layout may differ).
 */
export function extractHpxmlEnclosureAudit(doc) {
    const details = findBuildingDetails(doc);
    if (!details) {
        return { rows: {}, airInfiltration: null, error: "No BuildingDetails in HPXML." };
    }
    const enclosure = getChildrenByLocalName(details, "Enclosure")[0];
    if (!enclosure) {
        return { rows: {}, airInfiltration: null, error: "No Enclosure in HPXML." };
    }

    const walls = collectSurfaces(enclosure, "Walls", "Wall");
    const foundationWalls = collectSurfaces(enclosure, "FoundationWalls", "FoundationWall");
    const floors = collectSurfaces(enclosure, "Floors", "Floor");
    const roofs = collectSurfaces(enclosure, "Roofs", "Roof");
    const attics = collectSurfaces(enclosure, "Attics", "Attic"); //Maybe needed to differentiate between cathedral and flat ceilings
    const slabs = collectSurfaces(enclosure, "Slabs", "Slab");
    const rimJoists = collectSurfaces(enclosure, "RimJoists", "RimJoist");
    const windows = collectSurfaces(enclosure, "Windows", "Window");
    const skylights = collectSurfaces(enclosure, "Skylights", "Skylight");
    const doors = collectSurfaces(enclosure, "Doors", "Door");

    const floorAttic = floors.filter((f) => {
        const ext = getChildText(f, "ExteriorAdjacentTo");
        const int = getChildText(f, "InteriorAdjacentTo");
        return isAtticAdjacent(ext) || isAtticAdjacent(int);
    });

    // Gable ends and attached/common walls have no H2K equivalent, and pony walls live inside the
    // Volta basement, so all three are reported separately from the exposed above-grade wall area.
    const ponyWallLabels = getPonyWallLabels(foundationWalls, slabs);
    const attachedWalls = walls.filter((w) => isOtherHousingUnit(getChildText(w, "ExteriorAdjacentTo")));
    const gableWalls = walls.filter(
        (w) => !attachedWalls.includes(w) && isAtticAdjacent(getChildText(w, "InteriorAdjacentTo"))
    );
    const ponyWalls = walls.filter(
        (w) => !attachedWalls.includes(w) && !gableWalls.includes(w) && ponyWallLabels.has(getH2kLabel(w))
    );
    const exposedWalls = walls.filter(
        (w) => !attachedWalls.includes(w) && !gableWalls.includes(w) && !ponyWalls.includes(w)
    );

    const cathedralFlatCeilings = roofs.filter((r) => {
        const int = getChildText(r, "InteriorAdjacentTo");
        return int === "conditioned space";
    });

    const floorExposed = floors.filter((f) => {
        const ext = getChildText(f, "ExteriorAdjacentTo");
        const int = getChildText(f, "InteriorAdjacentTo");
        return ext === "outside" && int === "conditioned space";
    });

    const fwBasement = foundationWalls.filter((w) => {
        const ext = getChildText(w, "ExteriorAdjacentTo");
        const int = getChildText(w, "InteriorAdjacentTo");
        return mentionsBasement(ext) || mentionsBasement(int);
    });

    const fwCrawl = foundationWalls.filter((w) => {
        const ext = getChildText(w, "ExteriorAdjacentTo");
        const int = getChildText(w, "InteriorAdjacentTo");
        return mentionsCrawlspace(ext) || mentionsCrawlspace(int);
    });

    const slabBasement = slabs.filter((s) => {
        const int = getChildText(s, "InteriorAdjacentTo");
        return mentionsBasement(int);
    });

    const slabCrawl = slabs.filter((s) => {
        const int = getChildText(s, "InteriorAdjacentTo");
        return mentionsCrawlspace(int);
    });

    const slabOnGrade = slabs
        .filter((s) => {
            const int = getChildText(s, "InteriorAdjacentTo");
            return int === "conditioned space" || int === "garage";
        })
        .filter((s) => !slabBasement.includes(s) && !slabCrawl.includes(s));

    const windowR = (el) => getWindowUFactor(el) ?? getAssemblyEffectiveRValue(el) ?? getNominalRValue(el);
    const doorR = (el) => getDoorRValue(el) ?? getAssemblyEffectiveRValue(el) ?? getNominalRValue(el);
    const slabR = (el) => getSlabRValue(el) ?? getNominalRValue(el);

    const rows = {
        ceilingWithAttic: aggregateSurfaces(
            floorAttic,
            (el) => parseNumber(getChildText(el, "Area")),
            (el) => getAssemblyEffectiveRValue(el)
        ),
        cathedral: aggregateSurfaces(
            cathedralFlatCeilings,
            (el) => parseNumber(getChildText(el, "Area")),
            (el) => getAssemblyEffectiveRValue(el)
        ),
        wall: aggregateSurfaces(
            exposedWalls,
            (el) => parseNumber(getChildText(el, "Area")),
            (el) => getAssemblyEffectiveRValue(el)
        ),
        ponyWall: aggregateSurfaces(
            ponyWalls,
            (el) => parseNumber(getChildText(el, "Area")),
            (el) => getAssemblyEffectiveRValue(el)
        ),
        hpxmlGableWall: aggregateSurfaces(
            gableWalls,
            (el) => parseNumber(getChildText(el, "Area")),
            (el) => getAssemblyEffectiveRValue(el)
        ),
        hpxmlAttachedWall: aggregateSurfaces(
            attachedWalls,
            (el) => parseNumber(getChildText(el, "Area")),
            (el) => getAssemblyEffectiveRValue(el)
        ),
        expFloor: aggregateSurfaces(
            floorExposed,
            (el) => parseNumber(getChildText(el, "Area")),
            (el) => getAssemblyEffectiveRValue(el)
        ),
        floorHeader: aggregateSurfaces(
            rimJoists,
            (el) => parseNumber(getChildText(el, "Area")),
            (el) => getAssemblyEffectiveRValue(el)
        ),
        basementWall: aggregateSurfaces(
            fwBasement,
            (el) => parseNumber(getChildText(el, "Area")),
            (el) => getAssemblyEffectiveRValue(el)
        ),
        basementSlab: aggregateSurfaces(
            slabBasement,
            (el) => parseNumber(getChildText(el, "Area")),
            (el) => slabR(el)
        ),
        crawlspace: aggregateSurfaces(
            [...fwCrawl, ...slabCrawl],
            (el) => parseNumber(getChildText(el, "Area")),
            (el) => getAssemblyEffectiveRValue(el) ?? getNominalRValue(el)
        ),
        slab: aggregateSurfaces(
            slabOnGrade,
            (el) => parseNumber(getChildText(el, "Area")),
            (el) => slabR(el)
        ),
        // Window Area is total glass (including glass in doors). Door Area is opaque only.
        window: aggregateSurfaces(windows, (el) => parseNumber(getChildText(el, "Area")), windowR, "window"),
        skylights: aggregateSurfaces(skylights, (el) => parseNumber(getChildText(el, "Area")), windowR, "window"),
        door: aggregateSurfaces(doors, (el) => parseNumber(getChildText(el, "Area")), doorR),
    };

    const ai = enclosureChild(enclosure, "AirInfiltration");
    const measurement = ai ? getChildrenByLocalName(ai, "AirInfiltrationMeasurement")[0] : null;
    let airInfiltration = null;
    if (measurement) {
        airInfiltration = {
            typeOfTest: getChildText(measurement, "TypeOfTest"),
            housePressure: parseNumber(getChildText(measurement, "HousePressure")),
            unitOfMeasure: getChildText(measurement, "UnitOfMeasure"),
            airLeakage: getAirLeakageRate(measurement),
            effectiveLeakageArea: parseNumber(getChildText(measurement, "EffectiveLeakageArea")),
            specificLeakageArea: parseNumber(getChildText(measurement, "SpecificLeakageArea")),
        };
    }

    return { rows, airInfiltration, error: null };
}

function collectDeep(doc, localName) {
    const out = [];
    const walk = (node) => {
        if (!node) return;
        if (node.localName === localName) out.push(node);
        for (const c of node.children || []) walk(c);
    };
    walk(doc.documentElement);
    return out;
}

function heatingSystemTypeKey(el) {
    const wrapper = getChildrenByLocalName(el, "HeatingSystemType")[0];
    if (!wrapper) return getChildText(el, "HeatingSystemType");
    const kids = [...wrapper.children];
    if (kids.length === 1) return kids[0].localName || kids[0].textContent?.trim() || "";
    if (kids.length > 1)
        return kids
            .map((k) => k.localName)
            .filter(Boolean)
            .join(" / ");
    return wrapper.textContent?.trim() || "";
}

function firstChildChainText(el, ...localNames) {
    let node = el;
    for (const name of localNames) {
        const next = getChildrenByLocalName(node, name)[0];
        if (!next) return "";
        node = next;
    }
    return node.textContent?.trim() ?? "";
}

function looksLikeSecondaryHeating(typeStr) {
    const t = (typeStr || "").toLowerCase();
    return (
        t.includes("fireplace") ||
        t.includes("wood") ||
        t.includes("stove") ||
        t.includes("wall furnace") ||
        t.includes("space heater")
    );
}

function mapHeatingSystem(el) {
    const effPct =
        parseNumber(firstChildChainText(el, "AnnualHeatingEfficiency", "Value")) ??
        parseNumber(firstChildChainText(el, "AnnualHeatingEfficiency", "Percent")) ??
        parseNumber(getChildText(el, "AnnualHeatingEfficiency"));
    return {
        heatingSystemType: heatingSystemTypeKey(el),
        fuel: getChildText(el, "HeatingSystemFuel"),
        percentEfficiency: effPct,
        capacity: parseNumber(getChildText(el, "HeatingCapacity")),
        loadFraction: parseNumber(getChildText(el, "FractionHeatLoadServed")),
    };
}

function summarizePrimaryHeating(doc) {
    const systems = collectDeep(doc, "HeatingSystem");
    return systems
        .filter((el) => !looksLikeSecondaryHeating(heatingSystemTypeKey(el)))
        .map((el) => mapHeatingSystem(el));
}

function summarizeSecondaryHeating(doc) {
    const systems = collectDeep(doc, "HeatingSystem");
    return systems
        .filter((el) => looksLikeSecondaryHeating(heatingSystemTypeKey(el)))
        .map((el) => mapHeatingSystem(el));
}

function summarizeHeatPumps(doc) {
    const systems = collectDeep(doc, "HeatPump");
    return systems.map((el) => ({
        heatPumpType: getChildText(el, "HeatPumpType"),
        fuel: getChildText(el, "HeatPumpFuel"),
        heatingCapacity: parseNumber(getChildText(el, "HeatingCapacity")),
        coolingCapacity: parseNumber(getChildText(el, "CoolingCapacity")),
        heatingEfficiencyHspf:
            parseNumber(getChildText(el, "HeatingEfficiencyHSPF2")) ??
            parseNumber(getChildText(el, "HeatingEfficiencyHSPF")),
        coolingEfficiencySeer:
            parseNumber(getChildText(el, "CoolingEfficiencySEER2")) ??
            parseNumber(getChildText(el, "CoolingEfficiencySEER")),
        loadFractionHeat: parseNumber(getChildText(el, "FractionHeatLoadServed")),
        loadFractionCool: parseNumber(getChildText(el, "FractionCoolLoadServed")),
    }));
}

function summarizeCooling(doc) {
    const systems = collectDeep(doc, "CoolingSystem");
    return systems.map((el) => ({
        coolingSystemType: getChildText(el, "CoolingSystemType"),
        fuel: getChildText(el, "CoolingSystemFuel"),
        capacity: parseNumber(getChildText(el, "CoolingCapacity")),
        loadFraction: parseNumber(getChildText(el, "FractionCoolLoadServed")),
        seer:
            parseNumber(firstChildChainText(el, "AnnualCoolingEfficiency", "Value")) ??
            parseNumber(getChildText(el, "AnnualCoolingEfficiencySEER2")) ??
            parseNumber(getChildText(el, "AnnualCoolingEfficiencySEER")),
    }));
}

function summarizeVentilation(doc) {
    const fans = collectDeep(doc, "VentilationFan");
    return fans.map((el) => ({
        fanType: getChildText(el, "FanType"),
        testedFlowRate: parseNumber(getChildText(el, "TestedFlowRate")),
        ratedFlowRate: parseNumber(getChildText(el, "RatedFlowRate")),
        hoursPerDay: parseNumber(getChildText(el, "HoursInOperation")),
        fanPower: parseNumber(getChildText(el, "FanPower")),
        usedForWholeBuildingVent: getChildText(el, "UsedForWholeBuildingVentilation"),
        isSharedSystem: getChildText(el, "IsSharedSystem"),
    }));
}

function summarizeWaterHeating(doc) {
    const systems = collectDeep(doc, "WaterHeatingSystem");
    return systems.map((el) => ({
        fuelType: getChildText(el, "FuelType"),
        tankVolume: parseNumber(getChildText(el, "TankVolume")),
        firstHourRating: parseNumber(getChildText(el, "FirstHourRating")),
        energyFactor: parseNumber(getChildText(el, "EnergyFactor")),
        uniformEnergyFactor: parseNumber(getChildText(el, "UniformEnergyFactor")),
        cop: parseNumber(getChildText(el, "CoefficientOfPerformance")),
    }));
}

function summarizeDwhr(doc) {
    const nodes = collectDeep(doc, "DrainWaterHeatRecovery");
    return nodes.map((el) => ({
        efficiency: parseNumber(getChildText(el, "Efficiency")),
        equalFlow: getChildText(el, "EqualFlow"),
        preheatsTank: getChildText(el, "PreheatsTank"),
    }));
}

function summarizePv(doc) {
    const systems = collectDeep(doc, "PVSystem");
    return systems.map((el) => ({
        moduleType: getChildText(el, "ModuleType"),
        maxPowerOutput: parseNumber(getChildText(el, "MaxPowerOutput")),
        inverterEfficiency: parseNumber(getChildText(el, "InverterEfficiency")),
        yearModulesManufactured: parseNumber(getChildText(el, "YearModulesManufactured")),
    }));
}

export function extractHpxmlSystemsAudit(doc) {
    return {
        ventilation: summarizeVentilation(doc),
        spaceHeating: summarizePrimaryHeating(doc),
        secondaryHeating: summarizeSecondaryHeating(doc),
        heatPumps: summarizeHeatPumps(doc),
        cooling: summarizeCooling(doc),
        waterHeating: summarizeWaterHeating(doc),
        drainWaterHeatRecovery: summarizeDwhr(doc),
        photovoltaics: summarizePv(doc),
    };
}
